import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getAttribution } from "@/lib/attribution";
import { Seo } from "@/components/Seo";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, Loader2, ShieldCheck, AlertTriangle, Cog, ClipboardCheck, BookOpen } from "lucide-react";

const PPR = [
  { icon: Cog, label: "Process", color: "text-primary", text: "The repeatable way the work should happen — every time, by anyone." },
  { icon: ClipboardCheck, label: "Procedure", color: "text-green-600", text: "The manager's step-by-step for running and enforcing that process." },
  { icon: BookOpen, label: "Reference", color: "text-blue-600", text: "The form or record that proves it happened when it matters." },
];

/**
 * Reusable buyer-specific landing page. Same proven layout as the Service Manager page,
 * driven by role config so every shop/role feels this is built for them.
 */
export const BuyerLanding = ({
  testId,
  path,
  eyebrow,
  headline,
  subhead,
  toolkitTitle,
  pains,
  packs,
  browseLink, // { label, to }
  seoTitle,
  seoDescription,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [buying, setBuying] = useState(null);

  const buyStarter = async (id) => {
    if (!user) { navigate("/login", { state: { from: path } }); return; }
    setBuying(id);
    try {
      const attr = getAttribution();
      const { data } = await api.post("/checkout/starter", {
        starter_id: id,
        origin_url: window.location.origin,
        landing: attr.landing || path,
        utm_source: attr.utm_source || "",
      });
      window.location.href = data.url;
    } catch { toast.error("Could not start checkout."); setBuying(null); }
  };

  return (
    <div data-testid={testId}>
      <Seo
        title={seoTitle || `${headline} | Centennial`}
        description={seoDescription || subhead}
        canonicalPath={path}
      />
      {/* HERO */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{eyebrow}</span>
            <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-4 leading-[1.05]">
              {headline}
            </h1>
            <p className="text-white/75 text-lg mt-6 leading-relaxed">{subhead}</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <a href="#toolkit" data-testid="landing-cta-primary"
                className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3.5 font-semibold btn-lift">
                {toolkitTitle} <ArrowRight className="w-4 h-4" />
              </a>
              <Link to="/forms" className="inline-flex items-center gap-2 border border-white/20 px-6 py-3.5 font-semibold hover:border-white/50 transition-colors">
                Claim 3 forms free
              </Link>
            </div>
          </div>
          <div className="flex justify-center">
            <img src="/ppr-framework.png" alt="Process. Procedure. Reference — the CDOS framework"
              data-testid="ppr-graphic" className="w-full max-w-md bg-white rounded-lg p-4 shadow-2xl" />
          </div>
        </div>
      </section>

      {/* PAIN */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-8 py-20">
        <h2 className="font-heading font-black text-3xl tracking-tight max-w-2xl">What happens without documentation</h2>
        <div className="grid sm:grid-cols-2 gap-5 mt-10">
          {pains.map((p) => (
            <div key={p} className="flex items-start gap-3 border border-border bg-white p-6">
              <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <span className="text-foreground/85">{p}</span>
            </div>
          ))}
        </div>
      </section>

      {/* THE CDOS WAY (PPR) */}
      <section className="bg-secondary/50 border-y border-border">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">The CDOS way</span>
            <h2 className="font-heading font-black text-3xl tracking-tight mt-3">Every form is built on one simple idea</h2>
            <p className="text-muted-foreground mt-4">
              We don't hand you a pile of files. Each solution follows the same framework, so your whole team
              runs the same play — and you can prove it.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {PPR.map((s) => (
              <div key={s.label} className="bg-white border border-border p-7">
                <s.icon className={`w-7 h-7 ${s.color}`} />
                <h3 className="font-heading font-bold text-xl mt-4">{s.label}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section id="toolkit" className="max-w-[1280px] mx-auto px-6 lg:px-8 py-20">
        <h2 className="font-heading font-black text-3xl tracking-tight">{toolkitTitle}</h2>
        <p className="text-muted-foreground mt-3 max-w-2xl">Start where it hurts most today — each pack is a complete, curated solution. 30-day money-back guarantee.</p>
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          {packs.map((p) => (
            <div key={p.id} className="flex flex-col border border-border bg-white p-7" data-testid={`landing-pack-${p.id}`}>
              <h3 className="font-heading font-bold text-lg">{p.name}</h3>
              <p className="text-sm text-muted-foreground mt-2 flex-1">{p.desc}</p>
              <div className="font-heading font-black text-2xl mt-5">$49</div>
              <button onClick={() => buyStarter(p.id)} disabled={buying === p.id}
                data-testid={`landing-buy-${p.id}`}
                className="mt-4 inline-flex items-center justify-center gap-2 bg-primary text-white py-3 font-semibold btn-lift disabled:opacity-60">
                {buying === p.id ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : <>Get this pack <ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
          ))}
          <div className="flex flex-col border-2 border-primary bg-white p-7" data-testid="landing-toolkit">
            <span className="self-start text-[11px] uppercase tracking-wider font-semibold px-2.5 py-1 bg-primary text-white mb-4">Best value</span>
            <h3 className="font-heading font-bold text-lg">Manager's Toolkit</h3>
            <p className="text-sm text-muted-foreground mt-2 flex-1">The 20 most-used manager forms across HR, safety, security &amp; cash handling — ready to brand and deploy.</p>
            <div className="font-heading font-black text-2xl mt-5">$99</div>
            <Link to="/products/dealership-managers-toolkit"
              className="mt-4 inline-flex items-center justify-center gap-2 bg-primary text-white py-3 font-semibold btn-lift">
              View the toolkit <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
          <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary" /> 30-day money-back guarantee</span>
          <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Editable &amp; brandable files</span>
          <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Instant access</span>
        </div>

        {browseLink && (
          <Link to={browseLink.to} data-testid="landing-browse-link"
            className="inline-flex items-center gap-2 mt-8 text-primary font-semibold hover:underline">
            {browseLink.label} <ArrowRight className="w-4 h-4" />
          </Link>
        )}
      </section>

      {/* CTA BAND */}
      <section className="bg-primary text-white">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-8 py-16 text-center">
          <h2 className="font-heading font-black text-3xl tracking-tight">Not sure which forms you need?</h2>
          <p className="text-white/80 mt-3">Describe the situation and our Form Finder points you to the exact forms — free to try.</p>
          <Link to="/forms" data-testid="landing-cta-finder"
            className="inline-flex items-center gap-2 bg-white text-primary px-7 py-3.5 font-semibold mt-7 btn-lift">
            Find the forms you need <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};
